import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { activeActionIndex, activeCatalogSelector, addProductToCart, Catalog } from '@vi-succ/products-domain';
import { shoppingCartAddItem, shoppingCartCheckoutSuccess } from '@vi-succ/shoppingcart-domain';
import { filter, from, ignoreElements, map, switchMap, withLatestFrom } from 'rxjs';
import { AuthService } from '../../login/services/auth.service';
import { loadUserSuccess } from '../../login/state';
import { FirebaseService } from '../services/firebase.service';

@Injectable()
export class TrackingEffects {
  userProfileLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUserSuccess),
        withLatestFrom(this.authService.getUserIdentity().pipe(filter((userInfo) => !!userInfo))),
        switchMap(([action, { identityType, uuid }]) =>
          from(
            this.firebaseService.logEvent('user_profile_loaded', {
              identityType: identityType,
              userId: uuid,
              userProfileLoaded: true,
            }),
          ).pipe(ignoreElements()),
        ),
      ),
    { dispatch: false },
  );

  searchSpareparts$ = createEffect(
    () =>
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => this.router.routerState.snapshot.root.queryParams),
        filter((params) => !!params['query']),
        withLatestFrom(
          this.store.select(activeCatalogSelector).pipe(filter((c): c is Catalog => !!c)),
          this.authService.getUserIdentity(),
        ),
        switchMap(([params, catalog, identity]) =>
          from(
            this.firebaseService.logEvent('search_query', {
              query: params['query'],
              sales_org: catalog.salesOrg,
              userId: identity?.uuid ?? null,
              identityType: identity?.identityType ?? null,
            }),
          ).pipe(ignoreElements()),
        ),
      ),
    { dispatch: false },
  );

  trackTabView$ = createEffect(
    () =>
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => this.router.routerState.snapshot.root.queryParams),
        filter((params) => !!params['activeTab']),
        withLatestFrom(
          this.store.select(activeCatalogSelector).pipe(filter((c): c is Catalog => !!c)),
          this.authService.getUserIdentity(), // allow null/undefined
        ),
        switchMap(([params, catalog, identity]) => {
          const activeTab = params['activeTab'];
          return from(
            this.firebaseService.logEvent('tab_view', {
              tab_name: activeTab,
              sales_org: catalog.salesOrg,
              userId: identity?.uuid ?? null,
              identityType: identity?.identityType ?? null,
            }),
          ).pipe(ignoreElements());
        }),
      ),
    { dispatch: false },
  );

  switcherChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(activeActionIndex),
        withLatestFrom(
          this.store.select(activeCatalogSelector).pipe(filter((catalog): catalog is Catalog => !!catalog)),
          this.authService.getUserIdentity(), // allow null/undefined
        ),
        switchMap(([{ index }, catalog, identity]) =>
          from(
            this.firebaseService.logEvent('switcher_change', {
              sales_org: catalog.salesOrg,
              action: index === 0 ? 'spare_parts' : 'images',
              userId: identity?.uuid ?? null,
              identityType: identity?.identityType ?? null,
            }),
          ).pipe(ignoreElements()),
        ),
      ),
    { dispatch: false },
  );

  shoppingCartAddItem$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(shoppingCartAddItem),
        withLatestFrom(
          this.store.select(activeCatalogSelector).pipe(filter((catalog): catalog is Catalog => !!catalog)),
          this.authService.getUserIdentity(), // allow null/undefined
        ),
        switchMap(([{ product, quantity }, catalog, identity]) =>
          from(
            this.firebaseService.logEvent('add_item', {
              sales_org: catalog.salesOrg,
              material_number: product.materialNumber,
              quantity: quantity,
              userId: identity?.uuid ?? null,
              identityType: identity?.identityType ?? null,
            }),
          ).pipe(ignoreElements()),
        ),
      ),
    { dispatch: false },
  );

  addProductToCart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addProductToCart),
        withLatestFrom(
          this.store.select(activeCatalogSelector).pipe(filter((catalog): catalog is Catalog => !!catalog)),
          this.authService.getUserIdentity(), // allow null/undefined
        ),
        switchMap(([{ product, quantity }, catalog, identity]) =>
          from(
            this.firebaseService.logEvent('add_item', {
              sales_org: catalog.salesOrg,
              material_number: product.materialNumber,
              quantity: quantity,
              userId: identity?.uuid ?? null,
              identityType: identity?.identityType ?? null,
            }),
          ).pipe(ignoreElements()),
        ),
      ),
    { dispatch: false },
  );

  shoppingCartCheckout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(shoppingCartCheckoutSuccess),
        withLatestFrom(
          this.store.select(activeCatalogSelector).pipe(filter((catalog): catalog is Catalog => !!catalog)),
          this.authService.getUserIdentity(), // allow null/undefined
        ),
        switchMap(([{ shoppingCart }, catalog, identity]) =>
          from(
            this.firebaseService.logEvent('cart_checkout', {
              sales_org: catalog.salesOrg,
              cart_id: shoppingCart.id,
              userId: identity?.uuid ?? null,
              identityType: identity?.identityType ?? null,
            }),
          ).pipe(ignoreElements()),
        ),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private firebaseService: FirebaseService,
    private store: Store,
    private authService: AuthService,
    private router: Router,
  ) {}
}
